import React from "react";
import { useI18n } from "@/lib/i18n";
import { ArrowDown } from "lucide-react";

// Hero background — restore architectural interior look (user's own photo).
const HERO_BG = "/photos/p14.jpg";

export default function Hero({ onConsult, onScrollNext }) {
  const { t } = useI18n();

  return (
    <section
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden bg-onyx grain"
    >
      {/* Background image */}
      <img
        src={HERO_BG}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="hero-bg"
      />

      {/* Vignette overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-onyx/45 via-onyx/65 to-onyx" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_25%,_rgba(20,20,19,0.7)_100%)]" />

      {/* Top meta strip */}
      <div className="absolute top-24 left-0 right-0 z-10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between">
          <div className="eyebrow flex items-center gap-3">
            <span className="w-8 h-px bg-brass" />
            <span className="hidden sm:inline">{t.hero.eyebrow}</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-[0.65rem] tracking-[0.32em] uppercase text-bone/60">
            <span>EST. 2005</span>
            <span className="text-bone/20">·</span>
            <span>UA · 30+ міст</span>
          </div>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 min-h-[100svh] flex flex-col justify-end pb-24 lg:pb-32">
        <div className="grid grid-cols-12 gap-6">
          {/* Title */}
          <div className="col-span-12 lg:col-span-9">
            <h1
              data-testid="hero-title"
              className="font-display text-bone leading-[0.92] tracking-tighter text-[3.4rem] sm:text-[5.5rem] lg:text-[8.5rem] xl:text-[10.5rem] animate-fade-up"
              style={{ textShadow: "0 4px 40px rgba(0,0,0,0.6)" }}
            >
              <span className="block">Джавала</span>
              <span className="block italic-display text-brass">Мрамор</span>
            </h1>
          </div>

          {/* Right column meta */}
          <div className="col-span-12 lg:col-span-3 lg:pl-8 lg:border-l border-bone/20 flex flex-col justify-end">
            <p className="text-bone/85 leading-relaxed text-[0.95rem] sm:text-base max-w-md animate-fade-up [animation-delay:200ms]" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
              {t.hero.subtitle}
            </p>
          </div>
        </div>

        {/* CTAs + stats */}
        <div className="mt-12 lg:mt-16 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-7 flex flex-wrap gap-3 animate-fade-up [animation-delay:400ms]">
            <button
              data-testid="hero-primary-cta"
              onClick={onConsult}
              className="btn-brass"
            >
              {t.cta.inspection}
            </button>
            <a
              href="#gallery"
              data-testid="hero-secondary-cta"
              className="btn-ghost backdrop-blur-sm bg-onyx/30"
            >
              {t.cta.view_works}
            </a>
          </div>

          <div className="col-span-12 lg:col-span-5 grid grid-cols-3 gap-6 animate-fade-up [animation-delay:600ms]">
            {[t.stats.years, t.stats.objects, t.stats.warranty].map(([n, l], i) => (
              <div key={i} className="border-l border-brass/60 pl-4">
                <div className="font-display text-3xl sm:text-4xl text-bone leading-none" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}>{n}</div>
                <div className="text-[0.7rem] tracking-[0.2em] uppercase text-taupe mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <button
        onClick={onScrollNext}
        data-testid="hero-scroll-cue"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-bone/70 hover:text-brass transition-colors duration-500"
      >
        <span className="text-[0.65rem] tracking-[0.32em] uppercase">{t.hero.scroll}</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </button>

      {/* Side label */}
      <div className="hidden xl:block absolute right-8 top-1/2 -translate-y-1/2 vert eyebrow text-bone/50 z-10">
        20 років досвіду · Dustless · Premium care
      </div>
    </section>
  );
}
